import type { Session, User } from '@simplemon/lib';
import { organizationClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

// Determine the base URL for auth
function getAuthBaseURL() {
  // Check for explicitly configured API URL
  if (import.meta.env.VITE_API_URL) {
    return `${import.meta.env.VITE_API_URL}/api/auth`;
  }

  // In production, construct absolute URL from window.location
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}/api/auth`;
  }

  // Fallback for SSR or other edge cases
  return 'http://localhost:8790/api/auth';
}

// Create the auth client instance
export const authClient = createAuthClient({
  baseURL: getAuthBaseURL(),
  // Enable credentials for cross-origin requests
  fetchOptions: {
    credentials: 'include',
  },
  plugins: [organizationClient()],
});

// Export typed hooks
export const {
  signUp,
  signIn,
  signOut,
  useSession,
  getSession,
  requestPasswordReset,
  resetPassword,
  organization, // Export organization methods from the plugin
  useActiveOrganization, // Hook to get active organization (from org plugin)
} = authClient;

// Helper function to check if user is authenticated
export async function isAuthenticated(): Promise<boolean> {
  try {
    const result = await getSession();
    return !!result.data?.user;
  } catch {
    return false;
  }
}

// Type for auth context
export interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: typeof signIn;
  signUp: typeof signUp;
  signOut: typeof signOut;
}
